import { useCallback, useState } from 'react';
import { useAuth } from '../context';
import { useSwitchAccount } from './use-switch-account';

/**
 * Account switcher with a confirmation step: a pick becomes a pending entry the host confirms
 * or drops, rather than an immediate switch. Entries are master accounts and direct organizations.
 */
export function useAccountSwitchPrompt() {
  const { targets, activeTargetId, switchTo, switching } = useSwitchAccount();
  const { user } = useAuth();
  const [pendingAccountId, setPendingAccountId] = useState<string | null>(null);

  // Falls back to the session's own master account or account so the place being left is always named,
  // even if it is missing from the switcher's list.
  const sessionPlace = user?.currentMasterAccount ?? user?.account ?? null;
  const activeAccount =
    targets.find(target => target.id === activeTargetId) ??
    (sessionPlace ? { id: sessionPlace.id, name: sessionPlace.name } : null);

  const requestSwitch = useCallback(
    (accountId: string) => {
      // Re-picking the entry the session is already in is not a switch.
      setPendingAccountId(accountId === activeTargetId ? null : accountId);
    },
    [activeTargetId]
  );

  const cancelSwitch = useCallback(() => setPendingAccountId(null), []);

  const confirmSwitch = useCallback(async () => {
    if (!pendingAccountId) return;
    await switchTo(pendingAccountId);
    // A successful switch has already navigated away. A rejected one has not, so the prompt has
    // to close and leave the error toast visible.
    setPendingAccountId(null);
  }, [pendingAccountId, switchTo]);

  return {
    /** Every master account and organization the user can switch into, including the active one. */
    accounts: targets,
    activeAccountId: activeTargetId,
    activeAccount,
    pendingAccountId,
    /** The entry a confirmation is open for, or `null` when none is pending. */
    pendingAccount: targets.find(target => target.id === pendingAccountId) ?? null,
    switching,
    requestSwitch,
    confirmSwitch,
    cancelSwitch
  };
}
